import React, { useState, useEffect, useCallback } from "react";
import { partnersService, Partner, PartnerVoucher } from "../../services/partners";
import { PartnerVoucherSummary } from "../../services/api";
import {
  VOUCHER_STATUS_OPTIONS,
  voucherStatusLabel,
  voucherStatusBadgeClass,
  formatDateTime,
} from "./voucherDisplay";
import { useToast } from "../../shared/hooks/useToast";
import Toast from "../../shared/components/Toast";
import { redeemIssuedVoucher } from "./redeemVoucher";

interface VoucherManagementProps {
  onPageChange: (page: string) => void;
}

const emptySummary: PartnerVoucherSummary = {
  total: 0,
  available: 0,
  issued: 0,
  redeemed: 0,
  revoked: 0,
};

const VoucherManagement: React.FC<VoucherManagementProps> = ({
  onPageChange,
}) => {
  const { toast, showToast } = useToast();
  const [vouchers, setVouchers] = useState<PartnerVoucher[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [summary, setSummary] = useState<PartnerVoucherSummary>(emptySummary);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [batchFilter, setBatchFilter] = useState("");
  const [debouncedBatch, setDebouncedBatch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [redeemingId, setRedeemingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBatch(batchFilter.trim()), 300);
    return () => clearTimeout(timer);
  }, [batchFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, partnerFilter, debouncedBatch, rowsPerPage]);

  useEffect(() => {
    const loadPartners = async () => {
      try {
        const response = await partnersService.getPartners();
        setPartners(response.data || []);
      } catch {
        // Partner filter is optional; table can still load
      }
    };
    loadPartners();
  }, []);

  const fetchVouchers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await partnersService.getAllPartnerVouchers({
        status: statusFilter === "all" ? undefined : statusFilter,
        partner_id: partnerFilter === "all" ? undefined : partnerFilter,
        code: debouncedSearch || undefined,
        import_batch: debouncedBatch || undefined,
        page: currentPage,
        limit: rowsPerPage,
      });
      setVouchers(response.data || []);
      setSummary(response.summary || emptySummary);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.totalItems || 0);
    } catch (err: any) {
      setError(err.message || "Không thể tải danh sách voucher");
      setVouchers([]);
      setSummary(emptySummary);
    } finally {
      setIsLoading(false);
    }
  }, [
    statusFilter,
    partnerFilter,
    debouncedSearch,
    debouncedBatch,
    currentPage,
    rowsPerPage,
  ]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const handleRedeem = async (voucher: PartnerVoucher) => {
    await redeemIssuedVoucher({
      partnerId: voucher.partner_id,
      voucher,
      setRedeemingId,
      showToast,
      onUpdated: (updated) => {
        setVouchers((prev) =>
          prev.map((v) => (v._id === voucher._id ? { ...v, ...updated } : v))
        );
        fetchVouchers();
      },
    });
  };

  const startItem =
    totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  const summaryCards = [
    { key: "total", label: "Tổng", value: summary.total, color: "text-gray-900 dark:text-white" },
    { key: "available", label: "Có sẵn", value: summary.available, color: "text-blue-600 dark:text-blue-400" },
    { key: "issued", label: "Đã phát hành", value: summary.issued, color: "text-yellow-600 dark:text-yellow-400" },
    { key: "redeemed", label: "Đã đổi", value: summary.redeemed, color: "text-green-600 dark:text-green-400" },
    { key: "revoked", label: "Đã thu hồi", value: summary.revoked, color: "text-red-600 dark:text-red-400" },
  ];

  if (error && vouchers.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-600 dark:text-red-400 mb-4">{error}</div>
        <button
          onClick={fetchVouchers}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Thử lại
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Quản lý Voucher
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Xem và thao tác voucher từ tất cả đối tác
          </p>
        </div>
        <button
          onClick={() => onPageChange("partners")}
          className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Quản lý đối tác
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {summaryCards.map((card) => (
          <div
            key={card.key}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3"
          >
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {card.label}
            </div>
            <div className={`text-xl font-semibold mt-1 ${card.color}`}>
              {card.value.toLocaleString("vi-VN")}
            </div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px] max-w-sm relative">
          <input
            type="text"
            placeholder="Tìm theo mã voucher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        <select
          value={partnerFilter}
          onChange={(e) => setPartnerFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">Tất cả đối tác</option>
          {partners.map((partner) => (
            <option key={partner._id} value={partner._id}>
              {partner.name}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {VOUCHER_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.value === "all" ? "Tất cả trạng thái" : opt.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Batch import..."
          value={batchFilter}
          onChange={(e) => setBatchFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px]"
        />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            <span className="ml-3 text-gray-600 dark:text-gray-300">
              Đang tải voucher...
            </span>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Mã voucher
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Đối tác
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Họ tên
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      SĐT
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Trạng thái
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Batch import
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Phát hành
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Đổi quà
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {vouchers.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                      >
                        Không tìm thấy voucher nào
                      </td>
                    </tr>
                  ) : (
                    vouchers.map((voucher) => (
                      <tr
                        key={voucher._id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">
                          {voucher.code}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <button
                            type="button"
                            onClick={() =>
                              onPageChange(`partners/${voucher.partner_id}`)
                            }
                            className="text-blue-600 dark:text-blue-400 hover:underline text-left"
                          >
                            {voucher.partner_name || voucher.partner_id}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                          {voucher.prize_winner || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white font-mono whitespace-nowrap">
                          {voucher.phone || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${voucherStatusBadgeClass(
                              voucher.status
                            )}`}
                          >
                            {voucherStatusLabel(voucher.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {voucher.import_batch || "—"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {formatDateTime(voucher.issued_at)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                          {formatDateTime(voucher.redeemed_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {voucher.status === "issued" ? (
                            <button
                              type="button"
                              onClick={() => handleRedeem(voucher)}
                              disabled={redeemingId === voucher._id}
                              className="px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                            >
                              {redeemingId === voucher._id
                                ? "Đang xử lý..."
                                : "Đánh dấu đổi"}
                            </button>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-white dark:bg-gray-800 px-4 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Hiển thị
                </span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value={5}>5 dòng</option>
                  <option value={10}>10 dòng</option>
                  <option value={25}>25 dòng</option>
                  <option value={50}>50 dòng</option>
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Hiển thị {startItem}-{endItem} trong {totalItems} kết quả
                </span>

                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    &lt;
                  </button>

                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded ${
                          currentPage === pageNum
                            ? "bg-blue-600 text-white border-blue-600"
                            : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <Toast toast={toast} />
    </div>
  );
};

export default VoucherManagement;
